import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { staffApi, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ArrowLeft, ClipboardEdit, Save, X, User, Clock, Stethoscope, Sparkles, Lock, HeartPulse, Pencil, Trash2, Plus, History } from "lucide-react";
import { MEDICAL_QUESTIONS, COSMETIC_INGREDIENTS } from "@/lib/medicalQuestionnaire";
import { MASSAGE_HEALTH_ITEMS } from "@/pages/portal/MassagePreferenceStep";
import AttachmentThumbnails from "@/components/medical/AttachmentThumbnails";
import MedicalRecordDialog from "@/pages/staff/branch/calendar/MedicalRecordDialog";

/** Short Bali date (e.g. "12 Feb 2026") for the "Edited on …" chip. */
function formatBaliDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      timeZone: "Asia/Makassar", day: "numeric", month: "short", year: "numeric",
    });
  } catch { return ""; }
}

/** Format an ISO date "2026-08-20" as "August 20, 2026" for the
 *  accordion header. Falls back to the raw string on parse errors. */
function formatLongDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
}

/**
 * Care-Team-restricted Full Medical Profile.
 *
 * Tabbed layout — **Medical History** is the primary tab (default
 * open) followed by the editable **Questionnaire**. Contact details
 * are still absent from the backend response and no delete
 * affordance is ever rendered.
 */
export default function MyRosterClient() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const cid = params.get("cid");

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ q: {}, medication_allergies: "", cosmetic_allergies: [], current_medications: "" });
  const [massage, setMassage] = useState(null);   // latest Massage Preference Card
  // Jun-2026 · the Care Team portal had NO affordance to write or
  // correct a note from this page (the brand workspace page did), so a
  // missed note could not be entered here at all. Same dialog, same
  // endpoints, same permission gates as BrandCustomerDetail.
  const [editAppt, setEditAppt] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    if (!cid) {
      toast.error("Missing client id.");
      return;
    }
    setLoading(true);
    try {
      const [prof, mp] = await Promise.all([
        staffApi.get(`/staff/customer-medical-profile/${cid}`),
        staffApi.get(`/staff/customer-massage-preferences/${cid}`).catch(() => ({ data: { exists: false } })),
      ]);
      setProfile(prof.data);
      setDraft({
        q: prof.data.questionnaire || {},
        medication_allergies: prof.data.medication_allergies || "",
        cosmetic_allergies: prof.data.cosmetic_allergies || [],
        current_medications: prof.data.current_medications || "",
      });
      setMassage(mp.data || { exists: false });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  const setAnswer = (key, answer) =>
    setDraft((d) => ({ ...d, q: { ...d.q, [key]: { ...(d.q[key] || {}), answer } } }));
  const setNote = (key, note) =>
    setDraft((d) => ({ ...d, q: { ...d.q, [key]: { ...(d.q[key] || {}), note } } }));
  const toggleAllergy = (ing) =>
    setDraft((d) => {
      const s = new Set(d.cosmetic_allergies || []);
      if (s.has(ing)) s.delete(ing); else s.add(ing);
      return { ...d, cosmetic_allergies: [...s] };
    });

  const save = async () => {
    setSaving(true);
    try {
      const r = await staffApi.put(`/staff/customer-medical-profile/${cid}/questionnaire`, {
        medical_questionnaire: draft.q,
        medication_allergies: draft.medication_allergies,
        cosmetic_allergies: draft.cosmetic_allergies,
        current_medications: draft.current_medications,
      });
      setProfile(r.data);
      setEditing(false);
      toast.success("Medical questionnaire updated.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const canEdit = !!profile?.permissions?.can_edit;
  const canDelete = !!profile?.permissions?.can_delete;

  const openEdit = (h) => setEditAppt({
    id: h.appointment_id,
    customer_name: profile?.customer?.full_name || "",
    service_name: h.service_name,
    start_iso: h.start_iso || h.updated_at || h.created_at,
    status: h.status || "",
    checked_in_at: h.checked_in_at || (h.has_record ? (h.updated_at || h.created_at) : ""),
  });

  const confirmDelete = async (aid) => {
    if (!window.confirm("Delete this medical record? Linked-session siblings will also be cleared. This cannot be undone.")) return;
    setDeletingId(aid);
    try {
      await staffApi.delete(`/appointments/${aid}/medical-record`);
      toast.success("Medical record deleted");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-secondary/40">
      <header className="sticky top-0 z-10 bg-white border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button size="icon" variant="ghost" onClick={() => nav(-1)} data-testid="client-profile-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold truncate">Client Profile</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-5 pb-24">
        {loading && (
          <div className="animate-pulse space-y-3">
            <div className="h-24 bg-white rounded-xl" />
            <div className="h-40 bg-white rounded-xl" />
            <div className="h-72 bg-white rounded-xl" />
          </div>
        )}

        {!loading && profile && (
          <div className="space-y-4">
            {/* Identity — Age shown as a chip next to the name; no
                'contact details hidden' tagline (implicit from the
                backend contract). */}
            <section className="rounded-xl bg-white border border-border p-4" data-testid="client-profile-identity">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-lg font-semibold min-w-0 truncate" data-testid="client-profile-name">
                      {profile.customer.full_name}
                      {profile.customer.age != null ? (
                        <span className="ml-1 text-muted-foreground font-normal" data-testid="client-profile-age">
                          ({profile.customer.age})
                        </span>
                      ) : (
                        <span className="ml-2 text-xs text-muted-foreground font-normal" data-testid="client-profile-age">
                          Age: Unknown
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5" data-testid="client-profile-identity-line">
                    {profile.customer.gender ? profile.customer.gender[0].toUpperCase() + profile.customer.gender.slice(1) : "Gender not on file"}
                    <span data-testid="client-profile-nationality">
                      {profile.customer.nationality ? ` · ${profile.customer.nationality}` : " · Nationality not on file"}
                    </span>
                  </div>
                </div>
              </div>
            </section>

            <Tabs defaultValue="history" className="w-full">
              <TabsList className="bg-transparent p-0 border-b border-border rounded-none w-full justify-start gap-1 h-auto" data-testid="client-profile-tabs">
                <TabsTrigger
                  value="history"
                  className="border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent rounded-none px-3 py-2 gap-2"
                  data-testid="client-profile-tab-history"
                >
                  <Clock className="w-4 h-4" /> Medical History
                </TabsTrigger>
                <TabsTrigger
                  value="questionnaire"
                  className="border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent rounded-none px-3 py-2 gap-2"
                  data-testid="client-profile-tab-questionnaire"
                >
                  <Stethoscope className="w-4 h-4" /> Questionnaire
                </TabsTrigger>
                <TabsTrigger
                  value="massage"
                  className="border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent rounded-none px-3 py-2 gap-2"
                  data-testid="client-profile-tab-massage"
                >
                  <HeartPulse className="w-4 h-4" /> Massage Questionnaire
                </TabsTrigger>
              </TabsList>

              <TabsContent value="history" className="mt-4">
                <section className="rounded-xl bg-white border border-border p-4" data-testid="client-profile-history">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4 text-primary" />
                    <h2 className="text-sm font-semibold">Full Medical History</h2>
                    <span className="ml-auto text-[11px] text-muted-foreground" data-testid="client-profile-history-count">
                      {(profile.history_by_date || []).length} visit day{(profile.history_by_date || []).length === 1 ? "" : "s"}
                    </span>
                  </div>
                  {(profile.history_by_date || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No medical records saved yet across the brand.</p>
                  ) : (
                    <Accordion type="multiple" className="space-y-2" defaultValue={[(profile.history_by_date || [])[0]?.date].filter(Boolean)}>
                      {(profile.history_by_date || []).map((day) => {
                        // Group entries within the day by parent
                        // package_id OR link_group_id — Iter-291
                        // Nuclear Consolidation. Union-find keeps
                        // rows connected even when one carries
                        // link_group_id and another only carries
                        // package_id, matching the daily-worklist
                        // rule so the customer-profile timeline
                        // shows the same single-header collapse.
                        const parent = new Map();
                        const findRoot = (k) => {
                          if (!parent.has(k)) { parent.set(k, k); return k; }
                          let cur = k;
                          while (parent.get(cur) !== cur) cur = parent.get(cur);
                          return cur;
                        };
                        const unite = (a, b) => { parent.set(findRoot(a), findRoot(b)); };
                        for (const e of day.entries) {
                          const self = `A::${e.appointment_id}`;
                          findRoot(self);
                          if (e.link_group_id) unite(self, `L::${e.link_group_id}`);
                          if (e.package_id)    unite(self, `P::${e.package_id}`);
                        }
                        const groups = new Map();
                        for (const e of day.entries) {
                          const key = findRoot(`A::${e.appointment_id}`);
                          if (!groups.has(key)) groups.set(key, { key, package_name: e.package_name || "", entries: [] });
                          groups.get(key).entries.push(e);
                        }
                        const groupList = [...groups.values()];
                        const totalServices = day.entries.length;
                        const headerLabel = groupList.length === 1 && groupList[0].package_name
                          ? groupList[0].package_name
                          : groupList.length === 1
                            ? groupList[0].entries[0]?.service_name || "Session"
                            : `${totalServices} services`;
                        return (
                          <AccordionItem key={day.date} value={day.date} className="rounded-lg border border-border overflow-hidden" data-testid={`client-history-day-${day.date}`}>
                            <AccordionTrigger className="px-3 py-2.5 hover:no-underline" data-testid={`client-history-day-trigger-${day.date}`}>
                              <div className="flex-1 min-w-0 text-left">
                                <div className="text-sm font-semibold tabular-nums">{formatLongDate(day.date)}</div>
                                <div className="text-xs text-muted-foreground truncate">
                                  {headerLabel}
                                  <span className="ml-1 text-[11px]">· {totalServices} service{totalServices === 1 ? "" : "s"}</span>
                                </div>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-3 pb-3">
                              <div className="space-y-3">
                                {groupList.map((grp) => (
                                  <div key={grp.key} className="rounded-lg border border-border/70 bg-secondary/30 p-3">
                                    {grp.package_name && (
                                      <div className="flex items-center gap-1 text-[12px] font-medium text-primary mb-2">
                                        <Sparkles className="w-3.5 h-3.5" />
                                        <span className="truncate">{grp.package_name}</span>
                                      </div>
                                    )}
                                    <ul className="space-y-2">
                                      {grp.entries.map((h) => (
                                        <li key={h.appointment_id} className="text-xs" data-testid={`client-history-row-${h.appointment_id}`}>
                                          <div className="flex items-start justify-between gap-2 flex-wrap">
                                            <div className="text-sm font-medium min-w-0">
                                              {h.service_name}
                                              {h.team && h.team.length > 0 && (
                                                <span className="text-xs text-muted-foreground font-normal">
                                                  {" — "}
                                                  {h.team.map((t) => t.name).join(", ")}
                                                </span>
                                              )}
                                              {h.visit_cancelled && (
                                                <span
                                                  className="ml-1.5 text-[10px] font-semibold uppercase tracking-wider text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5"
                                                  data-testid={`client-history-cancelled-${h.appointment_id}`}
                                                  title="This visit was cancelled — the note below was recorded before it was cancelled."
                                                >
                                                  Cancelled visit
                                                </span>
                                              )}
                                              {(() => {
                                                const latestEdit = Array.isArray(h.edit_history) && h.edit_history.length
                                                  ? h.edit_history[h.edit_history.length - 1]
                                                  : null;
                                                if (!latestEdit) return null;
                                                return (
                                                  <span
                                                    className="ml-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 align-middle"
                                                    data-testid={`client-history-edited-${h.appointment_id}`}
                                                    title={`Previous notes: ${latestEdit.prev_session_notes || "(empty)"}`}
                                                  >
                                                    <History className="w-3 h-3" />
                                                    Edited on {formatBaliDate(latestEdit.edited_at)} by {latestEdit.edited_by_name || "staff"}
                                                  </span>
                                                );
                                              })()}
                                            </div>
                                            {h.has_record ? (
                                              (canEdit || canDelete) && (
                                                <div className="flex items-center gap-1 shrink-0">
                                                  {canEdit && (
                                                    <Button
                                                      size="sm"
                                                      variant="ghost"
                                                      className="h-7 px-2"
                                                      onClick={() => openEdit(h)}
                                                      data-testid={`client-history-edit-${h.appointment_id}`}
                                                      title="Edit this record"
                                                    >
                                                      <Pencil className="w-3.5 h-3.5" />
                                                    </Button>
                                                  )}
                                                  {canDelete && (
                                                    <Button
                                                      size="sm"
                                                      variant="ghost"
                                                      className="h-7 px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                                      onClick={() => confirmDelete(h.appointment_id)}
                                                      disabled={deletingId === h.appointment_id}
                                                      data-testid={`client-history-delete-${h.appointment_id}`}
                                                      title="Delete this record"
                                                    >
                                                      <Trash2 className="w-3.5 h-3.5" />
                                                    </Button>
                                                  )}
                                                </div>
                                              )
                                            ) : (
                                              canEdit && (
                                                <Button
                                                  size="sm"
                                                  variant="outline"
                                                  className="h-7 px-2 shrink-0 text-[11px] gap-1"
                                                  onClick={() => openEdit(h)}
                                                  data-testid={`client-history-add-${h.appointment_id}`}
                                                  title="Add the medical note for this session"
                                                >
                                                  <Plus className="w-3.5 h-3.5" /> Add note
                                                </Button>
                                              )
                                            )}
                                          </div>
                                          {(h.start_iso || h.branch_name) && (() => {
                                            let t = "";
                                            try {
                                              if (h.start_iso) t = new Date(h.start_iso).toLocaleTimeString("en-US", {
                                                timeZone: "Asia/Makassar", hour: "numeric", minute: "2-digit", hour12: true,
                                              });
                                            } catch { /* ignore */ }
                                            return (
                                              <div className="text-[11px] text-muted-foreground mt-0.5" data-testid={`client-history-loc-${h.appointment_id}`}>
                                                {t}
                                                {t && h.branch_name ? " — " : ""}
                                                {h.branch_name && <span className="font-medium">{h.branch_name}</span>}
                                              </div>
                                            );
                                          })()}
                              <div className="mt-1 whitespace-pre-wrap space-y-1">
                                {(h.session_notes || h.notes) && <p><b>Notes:</b> {h.session_notes || h.notes}</p>}
                                {h.internal_notes && (
                                  <p className="rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-amber-900">
                                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold mr-1">
                                      <Lock className="w-3 h-3" /> Internal
                                    </span>
                                    {h.internal_notes}
                                  </p>
                                )}
                                {h.treatments_done && <p><b>Treatments:</b> {h.treatments_done}</p>}
                                {h.follow_up && <p><b>Follow-up:</b> {h.follow_up}</p>}
                                {!h.has_record && (
                                  <p className="italic text-muted-foreground">No medical note yet for this session.</p>
                                )}
                                <AttachmentThumbnails
                                  attachments={h.attachments}
                                  testidPrefix={`client-history-attachments-${h.appointment_id}`}
                                />
                              </div>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  )}
                </section>
              </TabsContent>

              <TabsContent value="questionnaire" className="mt-4">
                <section className="rounded-xl bg-white border border-border p-4" data-testid="client-profile-questionnaire">
                  <div className="flex items-center gap-2 mb-3">
                    <Stethoscope className="w-4 h-4 text-primary" />
                    <h2 className="text-sm font-semibold">Medical Questionnaire</h2>
                    <div className="ml-auto">
                      {!editing ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditing(true)}
                          data-testid="client-profile-questionnaire-edit"
                          disabled={!profile.permissions?.can_edit}
                        >
                          <ClipboardEdit className="w-3.5 h-3.5 mr-1" /> Edit
                        </Button>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <Button size="sm" variant="ghost" onClick={() => { setEditing(false); load(); }} data-testid="client-profile-questionnaire-cancel">
                            <X className="w-3.5 h-3.5 mr-1" /> Cancel
                          </Button>
                          <Button size="sm" onClick={save} disabled={saving} data-testid="client-profile-questionnaire-save">
                            <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Saving…" : "Save"}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-3">
                    {MEDICAL_QUESTIONS.map((q) => {
                      const val = (editing ? draft.q : profile.questionnaire)[q.key] || {};
                      return (
                        <div key={q.key} className="border-b border-border/50 last:border-0 pb-2 last:pb-0">
                          <div className="text-sm font-medium">{q.label}</div>
                          {editing ? (
                            <>
                              <RadioGroup value={val.answer || ""} onValueChange={(v) => setAnswer(q.key, v)} className="flex gap-4 mt-1">
                                <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                                  <RadioGroupItem value="yes" data-testid={`q-${q.key}-yes`} /> Yes
                                </label>
                                <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                                  <RadioGroupItem value="no" data-testid={`q-${q.key}-no`} /> No
                                </label>
                              </RadioGroup>
                              <Textarea
                                className="mt-2 text-xs min-h-[52px]"
                                placeholder="Notes"
                                value={val.note || ""}
                                onChange={(e) => setNote(q.key, e.target.value)}
                                data-testid={`q-${q.key}-note`}
                              />
                            </>
                          ) : (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium mr-2 ${val.answer === "yes" ? "bg-amber-100 text-amber-800" : val.answer === "no" ? "bg-emerald-50 text-emerald-700" : "bg-secondary text-muted-foreground"}`}>
                                {val.answer ? val.answer.toUpperCase() : "—"}
                              </span>
                              {val.note ? <span>{val.note}</span> : <span className="italic">No note</span>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-4 grid gap-3">
                    <div>
                      <div className="text-sm font-medium">Medication allergies</div>
                      {editing ? (
                        <Input value={draft.medication_allergies} onChange={(e) => setDraft((d) => ({ ...d, medication_allergies: e.target.value }))} data-testid="med-allergies" />
                      ) : (
                        <p className="text-xs text-muted-foreground mt-1">{profile.medication_allergies || <span className="italic">None recorded</span>}</p>
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-medium">Cosmetic ingredient allergies</div>
                      {editing ? (
                        <div className="grid grid-cols-3 gap-2 mt-2">
                          {COSMETIC_INGREDIENTS.map((ing) => (
                            <label key={ing} className="flex items-center gap-1.5 text-xs cursor-pointer">
                              <Checkbox checked={(draft.cosmetic_allergies || []).includes(ing)} onCheckedChange={() => toggleAllergy(ing)} data-testid={`cosm-${ing}`} />
                              <span>{ing}</span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground mt-1">{(profile.cosmetic_allergies || []).join(", ") || <span className="italic">None flagged</span>}</p>
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-medium">Current medications</div>
                      {editing ? (
                        <Textarea value={draft.current_medications} onChange={(e) => setDraft((d) => ({ ...d, current_medications: e.target.value }))} data-testid="current-meds" />
                      ) : (
                        <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{profile.current_medications || <span className="italic">None</span>}</p>
                      )}
                    </div>
                  </div>
                </section>
              </TabsContent>

              <TabsContent value="massage" className="mt-4">
                <section className="rounded-xl bg-white border border-border p-4" data-testid="client-profile-massage">
                  <div className="flex items-center gap-2 mb-3">
                    <HeartPulse className="w-4 h-4 text-primary" />
                    <h2 className="text-sm font-semibold">Massage Preference Card</h2>
                    {massage?.exists && massage?.created_at && (
                      <span className="ml-auto text-[11px] text-muted-foreground" data-testid="client-profile-massage-updated">
                        Submitted {new Date(massage.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                      </span>
                    )}
                  </div>

                  {!massage?.exists ? (
                    <p className="text-xs text-muted-foreground italic" data-testid="client-profile-massage-empty">
                      This guest hasn't filled out a Massage Preference Card yet.
                    </p>
                  ) : (
                    <div className="space-y-4 text-sm">
                      <div className="grid grid-cols-3 gap-2" data-testid="client-profile-massage-pressure">
                        <div>
                          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Pressure</div>
                          <div className="mt-1 inline-flex items-center h-7 px-2.5 rounded-full bg-primary/10 text-primary text-xs font-medium capitalize">
                            {massage.pressure || "—"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Physically</div>
                          <div className="mt-1 text-sm tabular-nums font-medium" data-testid="client-profile-massage-physically">{massage.mood_physically}/10</div>
                        </div>
                        <div>
                          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Emotionally</div>
                          <div className="mt-1 text-sm tabular-nums font-medium" data-testid="client-profile-massage-emotionally">{massage.mood_emotionally}/10</div>
                        </div>
                      </div>

                      <div>
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Avoid</div>
                        {(massage.avoid_areas || []).length ? (
                          <div className="flex flex-wrap gap-1.5" data-testid="client-profile-massage-avoid">
                            {(massage.avoid_areas || []).map((a) => (
                              <span key={a} className="inline-flex items-center h-6 px-2 rounded-full bg-amber-50 text-amber-800 border border-amber-100 text-[11px]">{a}</span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground italic">None</p>
                        )}
                      </div>

                      <div>
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Focus</div>
                        {(massage.focus_areas || []).length ? (
                          <div className="flex flex-wrap gap-1.5" data-testid="client-profile-massage-focus">
                            {(massage.focus_areas || []).map((a) => (
                              <span key={a} className="inline-flex items-center h-6 px-2 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100 text-[11px]">{a}</span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground italic">None</p>
                        )}
                      </div>

                      <div className="border-t border-border/50 pt-3">
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Health conditions</div>
                        <div className="rounded-md border border-border overflow-hidden" data-testid="client-profile-massage-health">
                          {MASSAGE_HEALTH_ITEMS.map((q, i) => {
                            const row = (massage.health_conditions || {})[q.key] || {};
                            const ans = row.answer || "";
                            return (
                              <div key={q.key} className={`px-3 py-1.5 flex items-start gap-2 ${i > 0 ? "border-t border-border/50" : ""}`} data-testid={`client-profile-massage-health-${q.key}`}>
                                <span className={`inline-flex items-center h-5 px-1.5 rounded text-[10px] font-semibold shrink-0 ${ans === "yes" ? "bg-rose-100 text-rose-800" : ans === "no" ? "bg-emerald-50 text-emerald-700" : "bg-secondary text-muted-foreground"}`}>
                                  {ans ? ans.toUpperCase() : "—"}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium leading-tight">{q.label}</div>
                                  {ans === "yes" && (
                                    <div className="text-xs text-muted-foreground italic mt-0.5" data-testid={`client-profile-massage-health-${q.key}-note`}>
                                      {row.note || "No detail provided."}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="border-t border-border/50 pt-3">
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Spa Treatment Release</div>
                        <span className={`inline-flex items-center h-6 px-2 rounded-full text-[11px] ${massage.agreed_disclaimer ? "bg-emerald-50 text-emerald-800 border border-emerald-100" : "bg-amber-50 text-amber-800 border border-amber-100"}`} data-testid="client-profile-massage-disclaimer">
                          {massage.agreed_disclaimer ? "AGREED" : "NOT AGREED"}
                        </span>
                      </div>
                    </div>
                  )}
                </section>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </main>

      {editAppt && (
        <MedicalRecordDialog
          open={!!editAppt}
          onOpenChange={(o) => {
            if (!o) {
              setEditAppt(null);
              load();
            }
          }}
          appt={editAppt}
        />
      )}
    </div>
  );
}
